import { ChipColor } from './outcome-style.model';

export type LeadStage = 'new' | 'contacted' | 'appointment_set' | 'quoted' | 'won' | 'lost';

export const LEAD_STAGES: LeadStage[] = ['new', 'contacted', 'appointment_set', 'quoted', 'won', 'lost'];

/** Shared with the leads list and detail page so a stage reads the same color everywhere. */
export const LEAD_STAGE_COLORS: Record<LeadStage, ChipColor> = {
  new: 'primary',
  contacted: 'accent',
  appointment_set: 'accent',
  quoted: 'warning',
  won: 'success',
  lost: 'danger',
};

export type PreferredContactMethod = 'phone' | 'email' | 'text' | '';

/**
 * A line in a lead's rudimentary estimate. `label`/`unit_price` are
 * snapshotted from the service at the moment it was added, so a later change
 * to the service's `default_price` never rewrites a past lead's numbers.
 */
export interface LeadLineItem {
  service_id: string;
  label: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

/**
 * Created from an interaction with outcome_kind 'lead' — see design doc
 * §6.4. `services` stores the estimate line items (not bare ids);
 * `estimated_value` is their computed sum, written by LeadService so it can
 * never drift from the JSON.
 */
export interface Lead {
  id: string;
  address_id: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  preferred_contact_method: PreferredContactMethod;
  services: string; // JSON-encoded LeadLineItem[]
  estimated_value: number | null;
  currency: string;
  stage: LeadStage;
  lost_reason_id: string;
  assigned_to: string;
  follow_up_at: number | null;
  notes: string;
  exported_at: number | null;
  external_ref: string;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
  deleted_at: number | null;
}

export interface CreateLeadParams {
  address_id: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  preferred_contact_method?: PreferredContactMethod;
  line_items?: LeadLineItem[];
  currency?: string;
  assigned_to?: string;
  notes?: string;
}

export interface UpdateLeadParams {
  id: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  preferred_contact_method?: PreferredContactMethod;
  line_items?: LeadLineItem[];
  currency?: string;
  stage?: LeadStage;
  lost_reason_id?: string;
  assigned_to?: string;
  follow_up_at?: number | null;
  notes?: string;
}

/** Parses the stored line items; never throws — a malformed row degrades to an empty estimate. */
export function parseLeadServices(lead: Pick<Lead, 'services'>): LeadLineItem[] {
  try {
    const parsed = JSON.parse(lead.services);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Sums line totals — the single source of truth for what `estimated_value` should be. */
export function computeGrandTotal(lineItems: LeadLineItem[]): number {
  return lineItems.reduce((sum, item) => sum + item.line_total, 0);
}

/** A flat, human-readable summary for the CSV/XLSX export's services column. */
export function formatLineItemsSummary(lineItems: LeadLineItem[]): string {
  return lineItems
    .map((item) => (item.unit_price > 0 ? `${item.label} ×${item.quantity} ($${item.line_total.toFixed(2)})` : item.label))
    .join('; ');
}
